import type { AvailableScenario } from 'src/components/star-grid/components/VariancePairsPicker';
import type { DimensionItem } from 'src/components/star-grid/components/RowsDimensionPicker';
import { CreateReportFormValues, CreateReportOptions, DimensionValueMap } from 'src/components/star-grid/createReport.types';
import { CREATE_REPORT_DEFAULT_MEASURE, CREATE_REPORT_DEFAULT_SOURCE, CREATE_REPORT_MEASURES } from 'src/components/star-grid/createReportConstants';
import { PNL_ACTUALS_SCENARIO_ID } from 'src/components/star-grid/starGridConstants';
import { PnlHierarchyRef, PnlReportRequest, PnlScenarioEntry, PnlVariancePair } from 'src/components/star-grid/starGridTypes';

export const createReportMeasure = (measure: string) => CREATE_REPORT_MEASURES.find((option) => option === measure) ?? CREATE_REPORT_DEFAULT_MEASURE;

export const reportVarianceOptions = (values: CreateReportFormValues): AvailableScenario[] =>
  values.scenario_selections
    .filter((column) => column.scenario !== null)
    .map((column) => ({ id: column.id, label: column.scenario!.scenario_name, selectedMonths: column.selectedMonths }));

const reportScenarios = (values: CreateReportFormValues): PnlScenarioEntry[] =>
  values.scenario_selections
    .filter((column) => column.scenario !== null)
    .map((column) => ({
      reporting_scenario_id: Number(column.scenario!.reporting_scenario_id),
      label: column.scenario!.scenario_name.trim(),
      months: column.selectedMonths.map(Number),
      forecast_start_period:
        Number(column.scenario!.reporting_scenario_id) === PNL_ACTUALS_SCENARIO_ID || column.scenario!.forecast_start_period == null
          ? null
          : Number(column.scenario!.forecast_start_period)
    }));

const completeVariancePair = (pair: PnlVariancePair, options: readonly AvailableScenario[]): boolean => {
  const base = options.find((scenario) => scenario.id === pair.base_scenario);
  const compare = options.find((scenario) => scenario.id === pair.compare_scenario);
  return Boolean(
    base &&
      compare &&
      pair.base_months.length > 0 &&
      pair.base_months.length === pair.compare_months.length &&
      pair.base_months.every((month) => base.selectedMonths.includes(Number(month))) &&
      pair.compare_months.every((month) => compare.selectedMonths.includes(Number(month)))
  );
};

const reportVariancePairs = (values: CreateReportFormValues): PnlVariancePair[] => {
  const options = reportVarianceOptions(values);
  return values.variance_pairs
    .filter((pair) => completeVariancePair(pair, options))
    .map((pair) => ({
      ...pair,
      base_scenario: options.find((scenario) => scenario.id === pair.base_scenario)!.label,
      compare_scenario: options.find((scenario) => scenario.id === pair.compare_scenario)!.label,
      base_months: pair.base_months.map(Number),
      compare_months: pair.compare_months.map(Number)
    }));
};

/** Chips that carry a rollup hierarchy → the refs the API stores (with their picked leaves). */
const hierarchyRefs = (items: readonly DimensionItem[], picked: DimensionValueMap): PnlHierarchyRef[] =>
  items.filter((item) => item.hierarchyId).map((item) => ({ hierarchy_id: item.hierarchyId!, nodes: picked[item.hierarchyId!] ?? [] }));

export const createReportRequest = (values: CreateReportFormValues, options: CreateReportOptions): PnlReportRequest => ({
  report_name: values.report_name.trim(),
  report_description: values.report_description.trim() || null,
  group_by_dimensions: values.selected_dimensions.map((dimension) => dimension.field.trim()),
  filter_by_dimension: values.selected_filters.map((dimension) => dimension.field.trim()),
  scenarios: reportScenarios(values),
  measure: createReportMeasure(options.measure),
  variance_pairs: reportVariancePairs(values),
  source_mode: options.sourceMode === 'EXTERNAL' ? 'EXTERNAL' : CREATE_REPORT_DEFAULT_SOURCE,
  default_group_by: null,
  default_filters: {},
  // Hierarchy rollups, taken from the chips + the leaf values picked in each chip's dropdown.
  // fact_key is resolved server-side from dimension_registry.json, so only id + leaves are sent.
  row_hierarchies: hierarchyRefs(values.selected_dimensions, values.row_values),
  filter_hierarchies: hierarchyRefs(values.selected_filters, values.filter_values)
});
